/**
 * Auth Slice
 * Authentication state management
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TenantModules {
  playground?: boolean;
  activity?: {
    chatLogs?: boolean;
    leads?: boolean;
  };
  analytics?: {
    chats?: boolean;
    topics?: boolean;
    sentiment?: boolean;
  };
  sources?: {
    files?: boolean;
    text?: boolean;
    website?: boolean;
    qa?: boolean;
    notion?: boolean;
    suggestions?: boolean;
  };
  billing?: {
    amount?: boolean;
    ai?: boolean;
    activePlan?: boolean;
  };
  actions?: boolean;
  agents?: boolean;
  contacts?: boolean;
  deploy?: boolean;
  userManagement?: {
    users?: boolean;
    roles?: boolean;
    permissions?: boolean;
  };
  settings?: {
    general?: boolean;
    llmConfiguration?: boolean;
    whatsappConfiguration?: boolean;
    integrations?: boolean;
    security?: boolean;
  };
  campaigns?: boolean;
  /** Whether Settings → Instagram is available (backend flat module `instagram`). */
  instagram?: boolean;
  /** Whether Settings → LLM Configuration is available (backend flat module `llm`). */
  llm?: boolean;
  /** Automation (workflows) module. */
  automation?: boolean;
  /** Integrations (external gateway) module. */
  integrations?: boolean;
  /** Public Lead Capture Form module. */
  leadForm?: boolean;
  verticals?: boolean;
  leadScoring?: boolean;
}

/** Plan-tied boolean entitlements — see SubscriptionPlan.model.ts's `features` on the backend. */
export interface TenantFeatures {
  whatsappEnabled: boolean;
  instagramEnabled: boolean;
  llmConfigEnabled: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  sslEnabled: boolean;
  apiAccess: boolean;
  webhooksEnabled: boolean;
  campaignsEnabled: boolean;
  workflowsEnabled: boolean;
  enableRag: boolean;
  /** AI tool-calling — whether agents may take actions (send a file, record an
   *  appointment, capture a lead) rather than only reply. Already arrives fully
   *  resolved: auth.controller.ts sends `resolveTenantPlan().features`, which
   *  merges the plan default with any super-admin per-tenant limitOverride. */
  aiToolCalling: boolean;
  /** HTTP API Automation — the "Call API" / "Get User Input" flow nodes and
   *  the API Connections screen. Resolved server-side (plan default merged with
   *  any super-admin limitOverride), same as aiToolCalling. */
  httpAutomationEnabled: boolean;
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  planType: string;
  /** 'suspended' | 'inactive' = read-only workspace (banner + write-blocked dialog). */
  status?: 'active' | 'inactive' | 'suspended' | 'cancelled' | 'trial';
  modules?: TenantModules;
  features?: TenantFeatures;
}

/** Mirrors backend shared/rbac/permission-resolution.ts's EffectivePermissions
 *  exactly — 'ALL_ACCESS' for superadmin/tenant_owner (zero-I/O bypass on the
 *  backend), else the real per-module tier a custom or tenant_admin role was
 *  resolved to. Absent module key = no access to that module at all. */
export type PermissionAccessLevel = 'read' | 'edit' | 'full';
export type EffectivePermissions = 'ALL_ACCESS' | Record<string, PermissionAccessLevel>;

export interface User {
  id: string;
  email: string;
  firstName: string;
  // Optional: a Google sign-in can create an identity with no family_name
  // claim — see auth.api.ts's AuthUser for the full explanation.
  lastName?: string;
  phoneNumber?: string;
  /** Resolved URL of the profile photo ('' = none → initials). Never a storage key. */
  avatar?: string;
  role: string;
  tenantId?: string;
  /** @deprecated legacy flat module-key array, superseded by effectivePermissions — kept only because it's still echoed by the API, not read by any current logic. */
  permissions?: string[];
  /** The real RBAC access driving module visibility — see useModuleAccess.ts. */
  effectivePermissions?: EffectivePermissions;
}

export interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
}

// Initialize from localStorage if available
// Set loading to true initially to prevent flash of login page
const getInitialState = (): AuthState => {
  // Check if we're in browser environment
  if (typeof window === 'undefined') {
    return {
      user: null,
      tenant: null,
      token: null,
      isAuthenticated: false,
      loading: false,
    };
  }

  const token = localStorage.getItem('admin_token');
  const userStr = localStorage.getItem('admin_user');
  const tenantStr = localStorage.getItem('admin_tenant');

  if (token && userStr) {
    try {
      const user = JSON.parse(userStr);
      const tenant = tenantStr ? JSON.parse(tenantStr) : null;
      return {
        user,
        tenant,
        token,
        isAuthenticated: true,
        loading: false, // Already loaded from localStorage
      };
    } catch {
      // Invalid JSON, clear it
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_tenant');
    }
  }

  return {
    user: null,
    tenant: null,
    token: null,
    isAuthenticated: false,
    loading: false, // Not loading, ready to show login
  };
};

const initialState: AuthState = getInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; tenant?: Tenant; token: string }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.tenant = action.payload.tenant || null;
      state.isAuthenticated = true;
      localStorage.setItem('admin_token', action.payload.token);
      localStorage.setItem('admin_user', JSON.stringify(action.payload.user));
      if (action.payload.tenant) {
        localStorage.setItem('admin_tenant', JSON.stringify(action.payload.tenant));
      }
    },
    updateUser: (state, action: PayloadAction<{ user: User; tenant?: Tenant }>) => {
      state.user = action.payload.user;
      if (action.payload.tenant) {
        state.tenant = action.payload.tenant;
        localStorage.setItem('admin_tenant', JSON.stringify(action.payload.tenant));
      }
      localStorage.setItem('admin_user', JSON.stringify(action.payload.user));
    },
    updateTenant: (state, action: PayloadAction<{ tenant: Tenant }>) => {
      state.tenant = action.payload.tenant;
      localStorage.setItem('admin_tenant', JSON.stringify(action.payload.tenant));
    },
    updateToken: (state, action: PayloadAction<{ token: string }>) => {
      state.token = action.payload.token;
      localStorage.setItem('admin_token', action.payload.token);
    },
    logout: (state) => {
      state.user = null;
      state.tenant = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_tenant');
      localStorage.removeItem('refresh_token');
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export const { setCredentials, updateUser, updateTenant, updateToken, logout, setLoading } =
  authSlice.actions;
export default authSlice.reducer;
